import { Box, Text, TextProps } from '@chakra-ui/react';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';

type Props = {
  max?: number;
  min?: number;
  ready?: () => void;
} & PropsWithChildren &
  Omit<TextProps, 'fontSize'>;

export const TextFit: React.FC<Props> = ({
  max = 4,
  min = 1,
  ready = () => {},
  children,
  ...rest
}) => {
  const [fontSize, setFontSize] = useState(max);
  const textRef = useRef<HTMLParagraphElement>(null);
  const containerRef = useRef<HTMLElement>(null);
  const lastContentRef = useRef<string>('');

  useEffect(() => {
    const container = containerRef.current;
    const text = textRef.current;

    if (!container || !text) return;

    const currentContent = String(children);
    
    // Only recalculate if content actually changed
    if (currentContent === lastContentRef.current && fontSize !== max) {
      return;
    }
    lastContentRef.current = currentContent;

    const calculateSize = () => {
      const containerRect = container.getBoundingClientRect();
      const computedStyle = window.getComputedStyle(container);
      
      // Get padding from container (TextFit's Box)
      // Note: If TextFit's Box fills its parent (BoardBox) with 100% width/height,
      // then containerRect already accounts for BoardBox padding, so we only need
      // to account for TextFit's own padding (if any)
      const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
      const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
      const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
      const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
      
      // Available space is container size minus its own padding
      // Since TextFit Box fills BoardBox (100% width/height), containerRect
      // already excludes BoardBox padding, so we only subtract TextFit's padding
      const availableWidth = containerRect.width - paddingLeft - paddingRight;
      const availableHeight = containerRect.height - paddingTop - paddingBottom;
      
      if (availableWidth <= 0 || availableHeight <= 0) {
        // Container not ready, try again
        requestAnimationFrame(calculateSize);
        return;
      }

      // Get computed styles for accurate measurement
      const textComputedStyle = window.getComputedStyle(text);
      // Use a fixed base font size (16px) for em calculations
      // This ensures consistent sizing regardless of inherited font sizes
      const baseFontSize = 16;
      
      // Detect if text is likely to be single-line (like "$200") or multi-line (like categories)
      // Single-line text: numbers, short text - measure natural width
      // Multi-line text: longer text that might wrap - measure with width constraint
      // Categories should always allow wrapping for better scaling
      const isLikelySingleLine = /^\$[\d,]+$/.test(currentContent.trim());
      
      // Detect very long single words that need aggressive breaking
      const hasVeryLongWord = currentContent.split(/\s+/).some(word => word.length > 20);
      
      // Create a temporary span to measure text
      const measureEl = document.createElement('span');
      measureEl.style.visibility = 'hidden';
      measureEl.style.position = 'absolute';
      measureEl.style.whiteSpace = isLikelySingleLine ? 'nowrap' : 'normal';
      measureEl.style.wordWrap = 'break-word';
      // Use break-all for very long single words to prevent overflow
      measureEl.style.wordBreak = hasVeryLongWord ? 'break-all' : 'break-word';
      measureEl.style.overflowWrap = 'anywhere'; // Fallback for very long words
      measureEl.style.lineHeight = '1.0'; // Tighter line height to maximize vertical space
      if (!isLikelySingleLine) {
        measureEl.style.width = `${availableWidth}px`; // Constrain width for multi-line text to force wrapping
        measureEl.style.maxWidth = `${availableWidth}px`;
      }
      measureEl.style.fontFamily = textComputedStyle.fontFamily;
      measureEl.style.fontWeight = textComputedStyle.fontWeight;
      measureEl.style.fontStyle = textComputedStyle.fontStyle;
      measureEl.style.textTransform = textComputedStyle.textTransform;
      measureEl.style.textShadow = textComputedStyle.textShadow;
      measureEl.style.color = textComputedStyle.color;
      measureEl.style.fontSize = `${baseFontSize}px`; // Set base font size (1em)
      measureEl.textContent = currentContent;
      
      document.body.appendChild(measureEl);
      
      // Binary search to find maximum size that fits
      let low = min;
      let high = max;
      let optimalSize = min;
      const tolerance = 0.01;
      
      while (high - low > tolerance) {
        const mid = (low + high) / 2;
        // Set fontSize in em units (relative to baseFontSize)
        measureEl.style.fontSize = `${mid * baseFontSize}px`;
        
        // Force reflow
        measureEl.offsetHeight;
        
        const rect = measureEl.getBoundingClientRect();
        // Check both width and height for all text types
        // Use smaller tolerance to maximize space usage while preventing overflow
        const widthTolerance = 2;
        // Minimize tolerance: account for textShadow (10px) but use maximum vertical space
        const heightTolerance = 8;
        const fitsWidth = rect.width <= availableWidth + widthTolerance;
        const fitsHeight = rect.height <= availableHeight + heightTolerance;
        
        if (fitsWidth && fitsHeight) {
          // This size fits, try larger
          optimalSize = mid;
          low = mid;
        } else {
          // Too big, try smaller
          high = mid;
        }
      }
      
      document.body.removeChild(measureEl);
      
      const finalSize = Math.max(min, Math.min(max, optimalSize));
      setFontSize(finalSize);
      ready();
    };

    // Small delay to ensure layout is stable
    const timeoutId = setTimeout(calculateSize, 0);

    // Also recalculate on resize
    const resizeObserver = new ResizeObserver(() => {
      calculateSize();
    });

    resizeObserver.observe(container);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [children, max, min, ready, fontSize]);

  // Detect if this is likely multi-line text (categories)
  const isMultiLine = typeof children === 'string' && !/^\$[\d,]+$/.test(children.trim());
  const hasVeryLongWord = typeof children === 'string' && 
    children.split(/\s+/).some(word => word.length > 20);
  
  return (
    <Box
      overflow="hidden"
      position="relative"
      ref={containerRef}
      h="100%"
      w="100%"
      fontSize={`${fontSize * 16}px`}
      {...rest}
    >
      <Text 
        ref={textRef}
        whiteSpace={isMultiLine ? 'normal' : 'nowrap'}
        wordBreak={hasVeryLongWord ? 'break-all' : (isMultiLine ? 'break-word' : 'normal')}
        sx={{
          overflowWrap: hasVeryLongWord ? 'anywhere' : 'normal',
          wordWrap: isMultiLine ? 'break-word' : 'normal',
        }}
        lineHeight={isMultiLine ? '1.0' : '1'}
      >
        {children}
      </Text>
    </Box>
  );
};
